import { useCallback, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

import { reverseGeocodePublicCheckout } from "@/api";
import type { LatLng } from "@/components/Molecules/LocationPicker";
import type { CheckoutCustomerFormValues } from "@/lib/validation/checkout.schemas";

// Postal code is not filled: map data only has a district-wide code in Iran.
type AddressField = "province" | "city" | "address";

const ADDRESS_FIELDS: AddressField[] = ["province", "city", "address"];

export type GeocodeStatus = "idle" | "loading" | "filled" | "suggested" | "empty" | "error";

export type AddressSuggestion = Partial<Record<AddressField, string>>;

/**
 * Turns a map pin into an address and fills the form. A field is only overwritten
 * when it is empty or still holds what the map put there; anything the customer
 * typed is kept, and the map's version is offered as a suggestion instead.
 */
export function useAddressAutofill(token: string, form: UseFormReturn<CheckoutCustomerFormValues>) {
  const [status, setStatus] = useState<GeocodeStatus>("idle");
  const [suggestion, setSuggestion] = useState<AddressSuggestion | null>(null);
  const autoFilled = useRef<AddressSuggestion>({});
  const requestId = useRef(0);

  const lookup = useCallback(
    async (location: LatLng) => {
      const id = ++requestId.current;
      setStatus("loading");
      setSuggestion(null);

      try {
        const res = await reverseGeocodePublicCheckout(token, location);
        if (id !== requestId.current) return; // a newer pin superseded this one
        if (res.status !== 200) throw new Error(String(res.status));

        const result = res.data.data;
        const kept: AddressSuggestion = {};
        let filledAny = false;

        for (const field of ADDRESS_FIELDS) {
          const value = result[field]?.trim();
          if (!value) continue;
          const current = form.getValues(field)?.trim() ?? "";
          if (current === value) continue;

          if (!current || current === autoFilled.current[field]) {
            form.setValue(field, value, { shouldValidate: true, shouldDirty: true });
            autoFilled.current[field] = value;
            filledAny = true;
          } else {
            kept[field] = value;
          }
        }

        const hasSuggestion = Object.keys(kept).length > 0;
        setSuggestion(hasSuggestion ? kept : null);
        setStatus(hasSuggestion ? "suggested" : filledAny ? "filled" : "empty");
      } catch {
        if (id === requestId.current) setStatus("error");
      }
    },
    [form, token],
  );

  const applySuggestion = useCallback(() => {
    if (!suggestion) return;
    for (const field of ADDRESS_FIELDS) {
      const value = suggestion[field];
      if (!value) continue;
      form.setValue(field, value, { shouldValidate: true, shouldDirty: true });
      autoFilled.current[field] = value;
    }
    setSuggestion(null);
    setStatus("filled");
  }, [form, suggestion]);

  const dismissSuggestion = useCallback(() => {
    setSuggestion(null);
    setStatus("idle");
  }, []);

  return { status, suggestion, lookup, applySuggestion, dismissSuggestion };
}
